import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type ConfirmOptions = {
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Use destructive confirm button (deactivate / void / delete). */
  destructive?: boolean;
};

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

/** Promise-based confirm dialog — plan/27 calm chrome instead of window.confirm. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    setOpts(next);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  function settle(value: boolean) {
    setOpen(false);
    const resolve = resolveRef.current;
    resolveRef.current = null;
    resolve?.(value);
  }

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) settle(false);
        }}
      >
        <DialogContent
          onOpenAutoFocus={(e) => {
            // Prefer Cancel for destructive; Confirm otherwise.
            e.preventDefault();
            const root = e.currentTarget as HTMLElement;
            const prefer = opts?.destructive
              ? root.querySelector<HTMLButtonElement>("[data-confirm-cancel]")
              : root.querySelector<HTMLButtonElement>("[data-confirm-ok]");
            prefer?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>{opts?.title ?? "Confirm"}</DialogTitle>
            {opts?.description ? (
              <DialogDescription className="whitespace-pre-wrap">
                {opts.description}
              </DialogDescription>
            ) : null}
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              data-confirm-cancel
              onClick={() => settle(false)}
            >
              {opts?.cancelLabel ?? "Cancel"}
            </Button>
            <Button
              type="button"
              variant={opts?.destructive ? "destructive" : "default"}
              data-confirm-ok
              onClick={() => settle(true)}
            >
              {opts?.confirmLabel ?? "Continue"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within ConfirmProvider");
  }
  return ctx;
}
